import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ScheduleListItem } from '../../../models/api/query/query-schedules';
import { Action } from '../../../models/core/action';
import { BaseComponent } from '../../base/base.component';
import { ActionButtonComponent } from '../../core/buttons/action-button.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { ToolbarButtonComponent } from '../../core/buttons/toolbar-button.component';
import { SelectComponent } from '../../core/controls/select.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { EmptyStateComponent } from '../../core/panels/empty-state.component';
import { MenuComponent } from '../../core/panels/menu.component';
import { ProgressbarComponent } from '../../core/labels/progressbar.component';
import { ColumnComponent } from '../../core/table/column.component';
import { TableComponent } from '../../core/table/table.component';
import { QUERY_LIST_PAGE_SIZE_OPTIONS } from '../query-list-page-size-options';

@Component({
  selector: 'app-schedule-list',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TableComponent,
    ColumnComponent,
    ButtonComponent,
    ActionButtonComponent,
    ToolbarButtonComponent,
    SelectComponent,
    ActionRowComponent,
    EmptyStateComponent,
    MenuComponent,
    ProgressbarComponent,
  ],
  template: `
    <div>
      <div class="tw-flex tw-flex-col sm:tw-flex-row sm:tw-justify-between sm:tw-items-center tw-gap-2 tw-mb-4">
        <h2 class="tw-text-lg tw-font-semibold">Scheduled Queries</h2>
      </div>

      @if (retentionNotice) {
        <div
          class="tw-bg-[var(--sed-open-grey-color)] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-3 tw-mb-4 tw-flex tw-items-center tw-gap-2"
        >
          <i class="fa-light fa-circle-info" aria-hidden="true"></i>
          {{ retentionNotice }}
        </div>
      }

      <div class="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-end tw-gap-2 sm:tw-gap-0 tw-mb-4">
        <app-select
          class="tw-w-full sm:tw-w-1/3 tw-min-w-0"
          [control]="nextRunWithinControl"
          label="Next Run Within"
          [options]="dateRangeOptions"
          optionLabel="label"
          optionValue="value"
          [filter]="false"
          [showClear]="false"
        />
        <app-button class="tw-w-full sm:tw-w-1/3 tw-pb-2 tw-self-end" label="Search" icon="fa-light fa-search" (click)="search()" />
      </div>

      <div class="tw-flex tw-flex-row tw-w-full">
        @if (loading) {
          <app-empty-state
            height="300px"
            icon="fa-light fa-calendar"
            label="Loading schedules"
            message="Retrieving scheduled queries"
            class="tw-w-full"
          >
            <div class="tw-py-4">
              <app-progressbar />
            </div>
          </app-empty-state>
        } @else {
          <app-table
            class="tw-w-full"
            [dataSource]="schedules"
            [showRefresh]="true"
            (refresh)="loadSchedules(true)"
            [showSelection]="true"
            [selectionKey]="'scheduleId'"
            [isRowSelectable]="isScheduleSelectable"
            [showFilter]="true"
            [sortField]="sortField"
            [sortOrder]="sortOrder"
            recordLabel="Schedule"
            recordLabelPlural="Schedules"
            dataKey="scheduleId"
            [pageSize]="pageSize"
            [pageSizeOptions]="pageSizeOptions"
            [persistPaginationSettings]="true"
            tableId="query-view-schedules"
            (selectedEvent)="onSelectionChange($event)"
          >
            <app-column name="name" label="Name" [sort]="true" />
            <app-column name="description" label="Description" />
            <app-column name="queryTypeName" label="Type" [sort]="true" />
            <app-column name="nextRunDate" label="Next Run" [sort]="true">
              <ng-template #cell let-row>
                {{ row.nextRunDate | date : 'M/d/yyyy h:mm:ss a' }}
              </ng-template>
            </app-column>
            <app-column name="frequency" label="Frequency" [sort]="true" />
            <app-column name="actions" label="Actions">
              <ng-template #cell let-row>
                @if (row.isOwner) {
                  <span class="md:tw-hidden">
                    <app-action-button
                      [menu]="rowMenu"
                      [row]="row"
                      (click)="openRowMenu($event, row)"
                    />
                  </span>
                }
                <div class="tw-hidden md:tw-flex tw-gap-1">
                  @if (row.isOwner) {
                    <app-toolbar-button
                      type="Compact"
                      icon="fa-light fa-ban"
                      ariaLabel="Cancel Schedule"
                      (click)="cancelSchedule(row)"
                    />
                  }
                </div>
              </ng-template>
            </app-column>
          </app-table>
        }
      </div>

      <app-menu [items]="rowMenuItems" #rowMenu (click)="onRowAction($event)" />

      @if (selectedSchedules.length > 0) {
        <app-action-row class="tw-mt-4">
          <span left1>{{ selectedSchedules.length }} selected</span>
          <app-button
            right1
            label="Cancel Selected"
            icon="fa-light fa-ban"
            emphasis="Low"
            (click)="cancelSelected()"
          />
        </app-action-row>
      }
    </div>
  `,
  styles: ``,
})
export class ScheduleListComponent extends BaseComponent implements OnInit {
  @ViewChild('rowMenu')
  rowMenu: MenuComponent;

  schedules: ScheduleListItem[] = [];
  selectedSchedules: ScheduleListItem[] = [];
  loading = false;
  retentionNotice = '';
  pageSize = 10;
  pageSizeOptions = [...QUERY_LIST_PAGE_SIZE_OPTIONS];
  /** Classic View Schedules: NextRun descending — pre-sorted before bind. */
  sortField = 'nextRunDate';
  sortOrder = -1;

  isScheduleSelectable = (schedule: ScheduleListItem): boolean => schedule.isOwner;

  nextRunWithinControl = new FormControl('30d');

  dateRangeOptions = [
    { label: '30 Days', value: '30d' },
    { label: '60 Days', value: '60d' },
    { label: '6 Months', value: '6mo' },
    { label: '1 Year', value: '1yr' },
    { label: 'All', value: 'all' },
  ];

  rowMenuItems: Action[] = [];

  rowActions: Action[] = [{ label: 'Cancel Schedule', key: 'cancel' }];

  async ngOnInit(): Promise<void> {
    this.dispose.subs = this.nextRunWithinControl.valueChanges.subscribe(() => {
      void this.loadSchedules(false);
    });

    await this.loadSchedules(true);
  }

  async loadSchedules(showLoading = true): Promise<void> {
    if (showLoading) {
      this.loading = true;
    }
    try {
      const response = await this.api.query.getSchedules({
        nextRunWithin: this.nextRunWithinControl.value ?? '30d',
      });
      this.schedules = this.sortSchedulesByNextRunDesc(response.items);
      this.retentionNotice = response.retentionNotice;
      this.selectedSchedules = [];
    } finally {
      this.loading = false;
    }
  }

  /** Matches Classic QueryViewSchedulesPageController — NextRun descending. */
  private sortSchedulesByNextRunDesc(items: ScheduleListItem[]): ScheduleListItem[] {
    return [...items].sort(
      (a, b) => this.nextRunTime(b.nextRunDate) - this.nextRunTime(a.nextRunDate),
    );
  }

  private nextRunTime(value: string | null): number {
    return value ? new Date(value).getTime() : Number.NEGATIVE_INFINITY;
  }

  search(): void {
    void this.loadSchedules(false);
  }

  onSelectionChange(selected: ScheduleListItem[]): void {
    this.selectedSchedules = selected;
  }

  cancelSchedule(schedule: ScheduleListItem): void {
    this.confirm.show({
      label: 'Cancel Schedule',
      message: `Cancel schedule for "${schedule.name}"? This will stop all future scheduled runs.`,
      confirm: async () => {
        const response = await this.api.query.cancelSchedule(schedule.scheduleId);
        if (response.success) {
          await this.loadSchedules();
        }
      },
    });
  }

  openRowMenu(event: Event, row: ScheduleListItem): void {
    this.rowMenuItems = row.isOwner
      ? this.rowActions.map((a) => ({ label: a.label, key: a.key }))
      : [];
    this.rowMenu.items = this.rowMenuItems;
    this.rowMenu.toggle(event, row);
  }

  onRowAction(action: Action): void {
    const schedule = (this.rowMenu?.data ?? action.data) as ScheduleListItem;
    if (!schedule) return;

    if (action.key === 'cancel') {
      this.cancelSchedule(schedule);
    }
  }

  cancelSelected(): void {
    this.confirm.show({
      label: 'Cancel Schedules',
      message: `Cancel ${this.selectedSchedules.length} selected schedules? This will stop all future scheduled runs.`,
      confirm: async () => {
        for (const schedule of this.selectedSchedules) {
          await this.api.query.cancelSchedule(schedule.scheduleId);
        }
        this.selectedSchedules = [];
        await this.loadSchedules();
      },
    });
  }
}
